import { ComponentProps, ComponentPropsWithRef } from "react"
import { CaretRight } from "@phosphor-icons/react"
import { Link } from "react-router"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Badge } from "../ui/badge"
import { Show } from "./show"

type NavigableSetUpCardProps = SetUpCardProps & { to: string }

export const NavigableSetUpCard = ({
  to,
  ...props
}: NavigableSetUpCardProps) => (
  <Show when={!props.disabled} fallback={<SetUpCard {...props} />}>
    <Link to={to}>
      <SetUpCard {...props} />
    </Link>
  </Show>
)

type SetUpCardProps = ComponentProps<typeof Card>

export const SetUpCard: React.FC<SetUpCardProps> = ({
  children,
  className,
  ...props
}) => (
  <Card
    className={cn(
      `cursor-pointer shadow-none border border-gray-200 hover:border-blue-400 group h-full`,
      className
    )}
    {...props}
  >
    {children}
  </Card>
)

SetUpCard.displayName = `SetUpCard`

type SetUpCardBodyProps = ComponentProps<typeof CardHeader>

export const SetUpCardContent = ({
  children,
  className,
  ...props
}: SetUpCardBodyProps) => (
  <CardHeader className={cn(`h-full`, className)} {...props}>
    <div className="flex h-full gap-4 w-full">{children}</div>
  </CardHeader>
)

SetUpCardContent.displayName = `SetUpCardContent`

export const SetUpCardBody: React.FC<ComponentPropsWithRef<"div">> = ({
  ref,
  className,
  children,
  ...props
}) => (
  <div className={cn(`flex flex-col flex-1`, className)} ref={ref} {...props}>
    {children}
  </div>
)
SetUpCardContent.displayName = `SetUpCardContent`

export const SetUpCardHeader: React.FC<ComponentPropsWithRef<"div">> = ({
  ref,
  className,
  children,
  ...props
}) => (
  <div
    className={cn(`flex flex-col gap-2 mb-4`, className)}
    ref={ref}
    {...props}
  >
    {children}
  </div>
)

SetUpCardHeader.displayName = `SetUpCardHeader`

export const SetupCardIcon = () => (
  <div className="flex items-center">
    <CaretRight size={16} className="text-gray-500 flex-shrink-0" />
  </div>
)

export const SetUpCardTitle: React.FC<
  ComponentPropsWithRef<typeof CardTitle>
> = ({ ref, className, children, ...props }) => (
  <CardTitle
    className={cn(`text-lg font-medium`, className)}
    ref={ref}
    {...props}
  >
    {children}
  </CardTitle>
)

SetUpCardTitle.displayName = `SetUpCardTitle`

export const SetUpCardDescription: React.FC<
  ComponentPropsWithRef<typeof CardDescription>
> = ({ ref, className, children, ...props }) => (
  <CardDescription className={cn(`text-sm`, className)} ref={ref} {...props}>
    {children}
  </CardDescription>
)

SetUpCardDescription.displayName = `SetUpCardDescription`

export const SetUpCardBadge: React.FC<ComponentProps<typeof Badge>> = ({
  className,
  children,
  ...props
}) => (
  <Badge className={cn(`mt-auto self-start`, className)} {...props}>
    {children}
  </Badge>
)

export const SetUpCardBadgeGroup: React.FC<ComponentPropsWithRef<"div">> = ({
  ref,
  className,
  children,
  ...props
}) => (
  <div
    className={cn(`flex items-center gap-2 mt-auto self-start`, className)}
    ref={ref}
    {...props}
  >
    {children}
  </div>
)
